import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { GraduationCap, X, Send, Loader2, BookOpen, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import DictateButton from "@/components/DictateButton";
import FeedbackButtons from "@/components/FeedbackButtons";

/**
 * MaitreLeslexChat — Assistant IA contextuel pour LES-LEX FR.
 *
 * Architecture :
 *  - Bottom-LEFT floating bubble (🎓 icon) — right is for ReportIssueButton (🐛)
 *  - Streaming SSE depuis /api/help/ask (Claude Haiku + RAG sur 15 articles)
 *  - Affiche les articles sources (transparence)
 *  - Quota partagé avec Smart Triage (clé MONITORING, $5/mois)
 *
 * État persistent :
 *  - localStorage "maitre_conversations" : historique récent (last 20 messages)
 *  - localStorage "maitre_opened_once" : tooltip "Besoin d'aide ?" au premier login
 */

const GREETING = {
  role: "assistant",
  text: "Bonjour ! Je suis **Maître LES-LEX**, votre assistant IA pour la plateforme. 🎓\n\nPosez-moi toute question sur :\n- Comment générer un acte ou utiliser le Studio\n- Les 5 modèles IA et quand les utiliser\n- Due Diligence avec SIREN\n- Plans, facturation, RGPD\n- etc.\n\n*Je ne donne pas de conseil juridique — je vous guide dans l'utilisation de LES-LEX.*",
  sources: [],
};

export default function MaitreLeslexChat() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem("maitre_conversations");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [GREETING];
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Pulse animation on first visit
  useEffect(() => {
    try {
      const seen = localStorage.getItem("maitre_opened_once");
      if (!seen) {
        setShowPulse(true);
        const t = setTimeout(() => setShowPulse(false), 15000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  // Persist conversations (keep last 20 messages)
  useEffect(() => {
    try {
      const trimmed = messages.slice(-20);
      localStorage.setItem("maitre_conversations", JSON.stringify(trimmed));
    } catch {}
  }, [messages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const toggleOpen = useCallback(() => {
    setOpen((o) => !o);
    setShowPulse(false);
    try {
      localStorage.setItem("maitre_opened_once", new Date().toISOString());
    } catch {}
  }, []);

  const askStream = useCallback(async (question) => {
    setStreaming(true);
    const assistantMsgIdx = messages.length + 1; // index of the placeholder we'll insert
    setMessages((m) => [
      ...m,
      { role: "user", text: question },
      { role: "assistant", text: "", sources: [], streaming: true },
    ]);

    // Get JWT token
    const token = localStorage.getItem("les_lex_token");
    if (!token) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "❌ Vous devez être connecté pour utiliser Maître LES-LEX.",
          sources: [],
        };
        return copy;
      });
      setStreaming(false);
      return;
    }

    try {
      abortRef.current = new AbortController();
      const response = await fetch("/api/help/ask", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question,
          current_page: location.pathname,
        }),
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        // Fallback to sync endpoint
        await askSync(question, assistantMsgIdx);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE format: "data: {...}\n\n"
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const event = JSON.parse(payload);
            if (event.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, text: last.text + event.text };
                }
                return copy;
              });
            } else if (event.type === "sources") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, sources: event.sources || [] };
                }
                return copy;
              });
            } else if (event.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  text: `⚠️ ${event.message}`,
                  sources: [],
                };
                return copy;
              });
            } else if (event.type === "done") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = {
                    ...last,
                    streaming: false,
                    tokens: event.tokens_used,
                    cost: event.cost_usd,
                  };
                }
                return copy;
              });
            }
          } catch {
            // ignore parse errors
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            text: `❌ Erreur : ${err.message}`,
            sources: [],
          };
          return copy;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [messages.length, location.pathname]);

  // Non-streaming fallback
  const askSync = useCallback(async (question) => {
    const token = localStorage.getItem("les_lex_token");
    try {
      const res = await fetch("/api/help/ask-sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ question, current_page: location.pathname }),
      });
      const data = await res.json();
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: data.answer || data.error || "Aucune réponse.",
          sources: data.sources || [],
          tokens: data.tokens_used,
          cost: data.cost_usd,
        };
        return copy;
      });
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: `❌ Erreur : ${err.message}`,
          sources: [],
        };
        return copy;
      });
    }
  }, [location.pathname]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!input.trim() || streaming) return;
    const q = input.trim();
    setInput("");
    askStream(q);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const clearConversation = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([GREETING]);
    setStreaming(false);
  };

  // Suggested questions (context-aware)
  const suggestedQuestions = getSuggestedQuestions(location.pathname);

  return (
    <>
      {/* Floating bubble bottom-LEFT */}
      <button
        onClick={toggleOpen}
        className={`fixed bottom-6 left-6 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-primary to-primary/70 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group ${showPulse ? "animate-pulse" : ""}`}
        title="Maître LES-LEX — Assistant IA"
        aria-label="Ouvrir l'assistant Maître LES-LEX"
      >
        <GraduationCap className="h-6 w-6 group-hover:scale-110 transition-transform" />
        {showPulse && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 left-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-8rem)] bg-background border-2 border-primary/30 rounded-lg shadow-2xl flex flex-col animate-in slide-in-from-bottom-4">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-primary/20 p-1.5">
                <GraduationCap className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="font-semibold text-sm">Maître LES-LEX</div>
                <div className="text-[10px] text-muted-foreground">Assistant IA — Claude Haiku 4.5</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={clearConversation} className="h-7 w-7 p-0" title="Effacer la conversation">
                <Sparkles className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={toggleOpen} className="h-7 w-7 p-0" aria-label="Fermer">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/60"
                }`}>
                  {msg.role === "user" ? (
                    <div className="whitespace-pre-wrap">{msg.text}</div>
                  ) : (
                    <>
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:my-2">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.text || (msg.streaming ? "▊" : "")}
                        </ReactMarkdown>
                      </div>
                      {msg.sources && msg.sources.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-muted-foreground/20 text-[10px] text-muted-foreground">
                          <div className="flex items-center gap-1 mb-1">
                            <BookOpen className="h-3 w-3" />
                            <span>Sources :</span>
                          </div>
                          {msg.sources.slice(0, 3).map((s, si) => (
                            <div key={si} className="pl-4">
                              • {s.article_title} <span className="opacity-60">({Math.round(s.similarity * 100)}%)</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {msg.cost !== undefined && (
                        <div className="mt-1 text-[9px] text-muted-foreground opacity-60">
                          {msg.tokens} tokens · ${msg.cost}
                        </div>
                      )}
                      {!msg.streaming && msg.text && (
                        <div className="mt-1.5 pt-1.5 border-t border-muted-foreground/10">
                          <FeedbackButtons
                            source="maitre"
                            query={i > 0 ? (messages[i - 1]?.text || "") : ""}
                            aiResponse={msg.text}
                            metadata={{ model: "claude-haiku-4.5" }}
                            className="scale-90 origin-left"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {streaming && messages[messages.length - 1]?.role === "user" && (
              <div className="flex justify-start">
                <div className="bg-muted/60 rounded-lg px-3 py-2 text-sm">
                  <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                  <span className="text-muted-foreground">Maître LES-LEX réfléchit...</span>
                </div>
              </div>
            )}
          </div>

          {/* Suggested questions (if no active conversation) */}
          {messages.length <= 1 && suggestedQuestions.length > 0 && (
            <div className="px-3 py-2 border-t space-y-1">
              <div className="text-[10px] text-muted-foreground font-medium mb-1">
                💡 Questions courantes pour cette page :
              </div>
              <div className="flex flex-wrap gap-1">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); askStream(q); }}
                    className="text-[11px] px-2 py-1 bg-muted hover:bg-muted/80 rounded border border-border text-left"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Posez votre question... (🎤 dictée disponible)"
              rows={2}
              maxLength={1000}
              className="flex-1 text-sm resize-none min-h-[44px]"
              disabled={streaming}
            />
            <div className="self-end">
              <DictateButton
                onTranscript={(t) => setInput((prev) => prev + (prev && !prev.endsWith(" ") ? " " : "") + t)}
              />
            </div>
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || streaming}
              className="self-end"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}


// Context-aware suggested questions based on current page
function getSuggestedQuestions(pathname) {
  if (pathname.includes("/project/") && pathname.includes("/creator")) {
    return [
      "Comment générer une assignation ?",
      "Comment ajouter des pièces au bordereau ?",
      "Quels sont les 55 types de documents ?",
    ];
  }
  if (pathname.includes("/project/") && pathname.includes("/studio")) {
    return [
      "Comment utiliser l'Avocat du Diable ?",
      "Comment vérifier les citations ?",
      "Comment réviser avec l'IA ?",
    ];
  }
  if (pathname.includes("/due-diligence")) {
    return [
      "Comment faire un audit avec SIREN ?",
      "Quelles sont les 3 sources de données FR ?",
      "Comment interpréter le score de risque ?",
    ];
  }
  if (pathname.includes("/prediction")) {
    return [
      "Comment fonctionne la prédiction d'issue ?",
      "Quels sont les facteurs analysés ?",
      "Dans quels domaines est-elle fiable ?",
    ];
  }
  if (pathname.includes("/legal-search") || pathname.includes("/knowledge")) {
    return [
      "Comment chercher un article de loi ?",
      "Comment utiliser Judilibre ?",
      "Quelle est la différence entre RAG et recherche exacte ?",
    ];
  }
  if (pathname.includes("/pricing") || pathname.includes("/settings")) {
    return [
      "Quels sont les 5 plans disponibles ?",
      "Combien de tokens par document ?",
      "Comment annuler mon abonnement ?",
    ];
  }
  if (pathname.includes("/dashboard") || pathname === "/") {
    return [
      "Comment créer mon premier document ?",
      "Quelles sont les fonctions principales ?",
      "Comment importer des pièces PDF ?",
    ];
  }
  return [
    "Comment commencer avec LES-LEX ?",
    "Quels sont les 6 modèles IA utilisés ?",
    "Comment signaler un problème ?",
  ];
}
